/**
 * The founder's live state: GET /api/founder plus the event stream (`run`, `checks`, `heartbeat`) merged into one
 * value. Pure, so the merge rules are tested without a stream.
 */
import { ChecksEvent, Heartbeat, RunEvent, type Check, type FounderWorld, type TemplateName } from './schemas'

export interface FounderState {
  world: FounderWorld
  /** The latest heartbeat seen on the stream; GET /api/founder doesn't carry one. */
  heartbeat: Heartbeat | null
}

export type FounderEvent =
  | { type: 'world'; world: FounderWorld }
  | { type: 'run'; run: RunEvent }
  | { type: 'checks'; checks: Check[] }
  | { type: 'heartbeat'; heartbeat: Heartbeat }

/** One SSE frame as a typed event, or null for pings, unknown names and bodies that don't match the contract. */
export function parseFounderEvent(ev: { event: string; data: string }): FounderEvent | null {
  let raw: unknown
  try {
    raw = JSON.parse(ev.data)
  } catch {
    return null
  }
  if (ev.event === 'run') {
    const p = RunEvent.safeParse(raw)
    return p.success ? { type: 'run', run: p.data } : null
  }
  if (ev.event === 'checks') {
    // The contract sends the full list; accept `{ checks: [...] }` too.
    const list = raw && typeof raw === 'object' && !Array.isArray(raw) && 'checks' in raw ? (raw as { checks: unknown }).checks : raw
    const p = ChecksEvent.safeParse(list)
    return p.success ? { type: 'checks', checks: p.data } : null
  }
  if (ev.event === 'heartbeat') {
    const p = Heartbeat.safeParse(raw)
    return p.success ? { type: 'heartbeat', heartbeat: p.data } : null
  }
  return null
}

const PROVISIONING: TemplateName[] = ['full', 'adopt', 'boxOnly']

export function mergeFounder(state: FounderState, ev: FounderEvent): FounderState {
  switch (ev.type) {
    case 'world':
      return { ...state, world: ev.world }
    case 'checks':
      return { ...state, world: { ...state.world, checks: ev.checks } }
    case 'heartbeat': {
      if (state.heartbeat && state.heartbeat.at > ev.heartbeat.at) return state
      const box = state.world.box ? { ...state.world.box, lastHeartbeatAt: ev.heartbeat.at } : null
      return { world: { ...state.world, box }, heartbeat: ev.heartbeat }
    }
    case 'run': {
      const prev = state.world.run
      const next = ev.run
      if (prev && prev.id === next.id) {
        // An older frame arriving late (a reconnect replays the current state) never rolls the run back.
        if (prev.updatedAt && next.updatedAt && next.updatedAt < prev.updatedAt) return state
        return { ...state, world: { ...state.world, run: { ...prev, ...next, template: next.template ?? prev.template } } }
      }
      // A different run: only a provisioning run becomes "the latest provisioning run".
      if (!next.template || !PROVISIONING.includes(next.template)) return state
      return { ...state, world: { ...state.world, run: { ...next, template: next.template } } }
    }
  }
}

/** Settled step keys, to notice when a step finished. */
const settled = (w: FounderWorld) =>
  (w.run?.steps ?? []).filter((s) => s.status === 'done' || s.status === 'manual' || s.status === 'skipped').map((s) => s.key).join(',')

/**
 * Whether GET /api/founder should be read again after a merge: the box and app records change when steps finish,
 * and the events don't carry them.
 */
export function needsRefetch(before: FounderWorld, after: FounderWorld): boolean {
  if (before.run?.id !== after.run?.id) return true
  if (before.run?.status !== after.run?.status) return true
  if (settled(before) !== settled(after)) return true
  // A check turning ok can bring the preview address or the app record with it.
  const ok = (w: FounderWorld) => new Set(w.checks.filter((c) => c.status === 'ok').map((c) => c.key))
  const was = ok(before)
  return [...ok(after)].some((k) => !was.has(k))
}
